import { Injectable } from '@angular/core';

import { QueryParams } from '../api.service';
import { ListResult, SemantriaAPIService } from '../semantria-api.service';

export type SpotEScenarioType = 'DOCUMENT_SUMMARY' | 'DAILY_NEWSPAPER_V2' | string;

export interface ScenarioQueryParams {
  type?: SpotEScenarioType;
}

export interface SpotEScenario {
  scenario_id: string;
  name: string;
  version: number;
  notes: string;
  type: SpotEScenarioType;
  request_type: 'JSON' | string;
  response_type: 'TEXT' | string;
  parameters: [
    {
      name: string;
      type: 'STRING' | string;
      default_value: string;
      notes: string;
      required: boolean;
    },
  ];
  engine: string;
}

export interface SpotEScene {
  creator_user_id: string;
  modifier_user_id: string;
  scene_id: string;
  name: string;
  version: string;
}

export type MatchmakerHandlerPropertyName =
  | 'DOC_CARDINALTY'
  | 'CLICKABLE_POINTS'
  | 'CLICKABLE_POINTS_RULES'
  | 'ADDITIONAL_COMPARISONS'
  | 'BATCH_RELATIONSHIP'
  | 'SECTION_DATA'
  | 'RATING_REQUIRED'
  | 'RATING_CALCULATION'
  | 'TIME_SERIES_REQUIRED'
  | 'NICKNAME'
  | 'SHORT_PURPOSE'
  | 'LONG_PURPOSE'
  | 'QUESTION'
  | 'CONTENT_CHANNELS'
  | 'CONTAINERS'
  | 'EXCLUDED_ACCOUNTS'
  | 'ACCOUNTS'
  | 'PRODUCT_AREAS';

export type MatchmakerProperties = {
  [k in MatchmakerHandlerPropertyName]?: string | string[];
};

export interface MatchmakerHandlerProperty {
  property_key: MatchmakerHandlerPropertyName;
  property_value: string;
}

export type MatchmakerSpotlightParameterName =
  | 'aggregate_rating_score'
  | 'rating_score_high'
  | 'rating_score_low'
  | 'rating_field'
  | 'best_rating_value'
  | 'worst_rating_value'
  | 'metric_calculation'
  | 'box_options'
  | 'language'
  | 'analytic_item_type'
  | 'analytic_item_value'
  | 'sentiment_range'
  | 'sentiment_calculation'
  | 'sentiment'
  | 'breakdown_filter_field_name'
  | 'breakdown_filter_field_value'
  | 'driver_field_name'
  | 'comparison_field'
  | 'comparison_name'
  | 'content_channel'
  | 'query'
  | 'section_property'
  | 'date'
  | 'time_interval'
  | 'analytic_item_types'
  | 'analytic_item_values';

export type MatchmakerParameters = {
  [k in MatchmakerSpotlightParameterName]?: object | string | number;
};

export interface MatchmakerHandlerParameter {
  name: MatchmakerSpotlightParameterName;
  type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'LIST';
  default_value: string;
  notes?: string;
  required: boolean;
}

export interface MatchmakerHandler {
  handler_id: string;
  handler_name: string;
  handler_type: 'SCENARIO' | 'SCENE';
  question: string;
  context: any;
  properties: MatchmakerHandlerProperty[];
  parameters: MatchmakerHandlerParameter[];
}

@Injectable({
  providedIn: 'root',
})
export class SpotEService extends SemantriaAPIService {
  override getBaseUrl() {
    return `${super.getBaseUrl()}/spote`;
  }

  listScenarios(params: ScenarioQueryParams = {}) {
    return this.get<ListResult<SpotEScenario>>('/scenarios/', { ...params });
  }

  listScenes(params: ScenarioQueryParams = {}) {
    return this.get<ListResult<SpotEScene>>('/scenes/', { ...params });
  }

  matchmakerSearch(searchParams: MatchmakerProperties) {
    const properties: MatchmakerProperties = {
      ...searchParams,
      PRODUCT_AREAS: 'SPOTLIGHT',
    };
    const queryParams: QueryParams = {
      page_size: 100,
    };

    return this.post<ListResult<MatchmakerHandler>>(
      '/matchmaker/find/',
      {
        properties,
      },
      this.getBaseHeaders(),
      queryParams
    );
  }
}
